import { EditOutlined } from "@ant-design/icons"
import {
  Button,
  Descriptions,
  Divider,
  Drawer,
  Flex,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Typography,
  message,
} from "antd"
import { doc, serverTimestamp, updateDoc } from "firebase/firestore"
import moment from "moment"
import React, { useState } from "react"
import { db } from "../../firebase/config"
import { useAuthContext } from "../../hooks/useAuthContext"
import ShowKnifeInfo from "../../components/Knives/ShowKnifeInfo"
import useFetchMachineEmployeeList from "../../hooks/useFetchMachineEmployeeList"

const { Text } = Typography
const { TextArea } = Input

export default function WinderCompleteOrder({ data, winderSequence, setWinderSequence, disabled }) {
  const { user } = useAuthContext()
  const [open, setOpen] = useState(false)
  const [amountCreated, setAmountCreated] = useState(0)
  const [comment, setComment] = useState("")
  const [boxesCount, setBoxesCount] = useState(0)
  const [countPerRoll, setCountPerRoll] = useState(0)
  const [rollCount, setRollCount] = useState(0)
  const [weight, setWeight] = useState(0)
  const [rewinderEmployee, setRewinderEmployee] = useState(undefined)

  const { options: winderOptions, loading: winderLoading } = useFetchMachineEmployeeList("Winder")

  const showDrawer = () => {
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }

  const handleAmountChange = (value) => {
    setAmountCreated(value)
  }

  const handleBoxCount = (value) => {
    setBoxesCount(value)
  }

  const handleCountPerRoll = (value) => {
    setCountPerRoll(value)
  }
  const handleRollCount = (value) => {
    setRollCount(value)
  }
  const handleWeight = (value) => {
    setWeight(value)
  }

  // Function to update the order
  const updateOrder = async () => {
    const orderRef = doc(db, "orders", data.id)
    const winderSequenceRef = doc(db, "sequence", "documentForWinderSequence") // Replace with your actual sequence document ID

    try {
      // Step 1: Update the order document
      await updateDoc(orderRef, {
        updated: serverTimestamp(),
        amountCreated: amountCreated,
        rewinder: user.uid,
        rewinderEmail: user.email,
        doneByRewinder: serverTimestamp(),
        orderStatus: 6,
        rewinderComment: comment,
        rewinderCountPerRoll: countPerRoll,
        rewinderWeight: weight,
        rewinderRollCount: rollCount,
        boxesCount: boxesCount,
        rewinderEmployee: rewinderEmployee || null,
      })

      // Step 2: Update the winder sequence document by removing the ID
      const updatedSequence = winderSequence.filter((orderId) => orderId !== data.id)

      await updateDoc(winderSequenceRef, {
        winderSequence: updatedSequence, // Assuming "winderSequence" is the field that stores the winder sequence array
      })

      // Step 3: Update the local state (optional, depending on your application)
      setWinderSequence(updatedSequence)

      message.success("Updated successfully!")
    } catch (err) {
      message.error("Error updating order: " + err.message)
    } finally {
      setOpen(false)
    }
  }

  // Handler with security check logic.
  const handleUpdateOrder = () => {
    // Check if the created amount is less than the order's label count
    if (amountCreated < data.labelCount) {
      Modal.confirm({
        title: "Confirm Update",
        content: `Is this correct? Amount you created is ${amountCreated} but the order needs ${data.labelCount}.`,
        okText: "Yes",
        cancelText: "No",
        onOk: async () => {
          await updateOrder()
        },
      })
    } else {
      updateOrder()
    }
  }

  return (
    <div>
      <Button disabled={disabled} size="small" type="primary" onClick={showDrawer}>
        Complete <EditOutlined />
      </Button>
      <Drawer title="Order Fill" placement="left" width={500} onClose={onClose} open={open}>
        <Divider style={{ borderColor: "black" }}>Rewinder Info</Divider>
        <Descriptions column={1}>
          <Descriptions.Item labelStyle={{ alignSelf: "center" }} label="Amount Created">
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              onChange={handleAmountChange}
              formatter={(value) =>
                value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") : ""
              }
              parser={(value) => (value ? value.replace(/\s/g, "") : "")}
            />
          </Descriptions.Item>
          <Descriptions.Item labelStyle={{ alignSelf: "center" }} label="Box Count">
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              onChange={handleBoxCount}
              formatter={(value) =>
                value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") : ""
              }
              parser={(value) => (value ? value.replace(/\s/g, "") : "")}
            />
          </Descriptions.Item>
          <Descriptions.Item labelStyle={{ alignSelf: "center" }} label="Count per Roll">
            <InputNumber
              min={0}
              placeholder="1000 (amount of count per 1 roll)"
              style={{ width: "100%" }}
              onChange={handleCountPerRoll}
              formatter={(value) =>
                value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") : ""
              }
              parser={(value) => (value ? value.replace(/\s/g, "") : "")}
            />
          </Descriptions.Item>
          <Descriptions.Item labelStyle={{ alignSelf: "center" }} label="How many Rolls">
            <InputNumber
              min={0}
              placeholder="10 (how many rolls were created)"
              style={{ width: "100%" }}
              onChange={handleRollCount}
              formatter={(value) =>
                value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") : ""
              }
              parser={(value) => (value ? value.replace(/\s/g, "") : "")}
            />
          </Descriptions.Item>
          <Descriptions.Item labelStyle={{ alignSelf: "center" }} label="Weight for 1 roll">
            <InputNumber
              min={0}
              placeholder="example : 1000 (grams)"
              style={{ width: "100%" }}
              onChange={handleWeight}
              formatter={(value) =>
                value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") : ""
              }
              parser={(value) => (value ? value.replace(/\s/g, "") : "")}
            />
          </Descriptions.Item>
          <Descriptions.Item labelStyle={{ alignSelf: "center" }} label="Employee">
            <Select
              value={rewinderEmployee}
              placeholder={winderLoading ? "Loading..." : "Select rewinder"}
              loading={winderLoading}
              onChange={setRewinderEmployee}
              options={winderOptions}
              style={{ width: "100%" }}
              allowClear
            />
          </Descriptions.Item>
          <Descriptions.Item label="Comment ">
            <TextArea onChange={(event) => setComment(event.target.value)} />
          </Descriptions.Item>
        </Descriptions>

        <Button
          block
          type="primary"
          disabled={
            !rewinderEmployee ||
            amountCreated <= 0 ||
            rollCount <= 0 ||
            countPerRoll <= 0 ||
            countPerRoll <= 0 ||
            weight <= 0
          }
          onClick={handleUpdateOrder}>
          Update Order
        </Button>
        <Divider style={{ borderColor: "black" }}>Technical Page</Divider>
        <Flex justify="center" align="center">
          <Image src={data.technicalPage} width="50%" />
        </Flex>
        <Divider style={{ borderColor: "black" }}>Comments</Divider>
        <Descriptions column={1}>
          <Descriptions.Item label="Manager">
            <Text>{data && data.salesComment}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Designer">
            <Text>{data && data.designerComment}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Machine">
            <Text>{data && data.machineCompletion.finishComment}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Paper Manager">
            <Text>{data?.paperManagerComment || ""}</Text>
          </Descriptions.Item>
        </Descriptions>
        <Divider style={{ borderColor: "black" }}>Order Details</Divider>
        <Descriptions column={1}>
          <Descriptions.Item label="Order Number">{data && data.orderNumber}</Descriptions.Item>
          <Descriptions.Item label="Client">{data && data.client.client}</Descriptions.Item>
          <Descriptions.Item label="Product">
            {data && data.selectedProduct.product.productName}
          </Descriptions.Item>
          <Descriptions.Item label="Order Amount">{data && data.labelCount}</Descriptions.Item>
          <Descriptions.Item label="Width X Height">
            {data && data.selectedProduct.labelWidth} X {data && data.selectedProduct.labelHeight}
          </Descriptions.Item>
          <Descriptions.Item label="Knife">
            {data && data.selectedProduct.knifeCode !== "none" ? (
              <ShowKnifeInfo props={data && data.selectedProduct} />
            ) : (
              "New Knife"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {data && moment.unix(data.created.seconds).format("DD/MM/YYYY")}
          </Descriptions.Item>
        </Descriptions>
      </Drawer>
    </div>
  )
}
